import type { components } from "@/api-schema";
import {
  branchToAddressFormValues,
  isAddressFormEmpty,
} from "@/components/address-form-fields/_helpers/address-form-values";
import { AddressFormFields } from "@/components/address-form-fields/address-form-fields";
import type { AddressFormValues } from "@/components/address-form-fields/address-form-schema";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox, type ComboboxPaginationState } from "@/components/ui/combobox";
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type {
  MappedBranch,
  RelocationOrderAdminFormSchemaType,
} from "@/modules/fit/internal/relocation-order/admin/_components/relocation-order-admin-form-fields";
import { Copy } from "lucide-react";
import { useMemo } from "react";
import type { SetValueConfig, UseFormReturn } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

type Branch = components["schemas"]["Branch"];
type AddressField = keyof AddressFormValues;

export enum RelocationOrderLocationKind {
  Origin = "origin",
  Current = "current",
  Destination = "destination",
}

const LOCATION_CONFIG = {
  [RelocationOrderLocationKind.Origin]: {
    branchField: "originBranch",
    addressField: "originAddress",
    titleKey: "fit:Origin",
    branchLabelKey: "fit:OriginBranch",
    branchPlaceholderKey: "fit:SelectOriginBranch",
  },
  [RelocationOrderLocationKind.Current]: {
    branchField: "currentBranch",
    addressField: "currentAddress",
    titleKey: "fit:CurrentBranch",
    branchLabelKey: "fit:CurrentBranch",
    branchPlaceholderKey: "fit:SelectCurrentBranch",
  },
  [RelocationOrderLocationKind.Destination]: {
    branchField: "destinationBranch",
    addressField: "destinationAddress",
    titleKey: "fit:Destination",
    branchLabelKey: "fit:DestinationBranch",
    branchPlaceholderKey: "fit:SelectDestinationBranch",
  },
} as const satisfies Record<
  RelocationOrderLocationKind,
  {
    branchField: keyof RelocationOrderAdminFormSchemaType;
    addressField: keyof RelocationOrderAdminFormSchemaType;
    titleKey: string;
    branchLabelKey: string;
    branchPlaceholderKey: string;
  }
>;

const ADDRESS_FIELDS: AddressField[] = [
  "recipientName",
  "company",
  "street",
  "houseNumber",
  "houseExtension",
  "postalCode",
  "city",
  "country_code",
  "latitude",
  "longitude",
];

const FORM_UPDATE_OPTIONS: SetValueConfig = {
  shouldDirty: true,
  shouldValidate: true,
};

type RelocationOrderLocationCardProps = {
  kind: RelocationOrderLocationKind;
  form: UseFormReturn<RelocationOrderAdminFormSchemaType>;
  branchOptions: MappedBranch[];
  branchComboboxState?: ComboboxPaginationState;
  selectedBranch?: Branch;
};

export function RelocationOrderLocationCard({
  kind,
  form,
  branchOptions,
  branchComboboxState,
  selectedBranch,
}: RelocationOrderLocationCardProps) {
  const { t } = useTranslation(["common", "fit"]);
  const { branchField, addressField, titleKey, branchLabelKey, branchPlaceholderKey } = LOCATION_CONFIG[kind];
  const branch = useWatch({ control: form.control, name: branchField });
  const address = useWatch({ control: form.control, name: addressField });
  const canCopyAddressFromBranch = !!selectedBranch?.address;
  const hasBranch = (branch ?? "").trim().length > 0;
  const hasAddress = !isAddressFormEmpty(address);
  const hasMismatch = useMemo(() => {
    if (!hasBranch || !hasAddress || !selectedBranch?.address) {
      return false;
    }

    const branchValues = branchToAddressFormValues(selectedBranch);

    return ADDRESS_FIELDS.some((field) => (address?.[field] ?? "").trim() !== (branchValues[field] ?? "").trim());
  }, [hasBranch, hasAddress, address, selectedBranch]);
  const addressModeBadge =
    hasBranch && hasAddress && hasMismatch
      ? {
          label: t("fit:AddressModeBranchAndCustomAddress"),
          tooltip: t("fit:AddressModeBranchAndCustomAddressTooltip"),
        }
      : !hasBranch && hasAddress
        ? {
            label: t("fit:AddressModeExternalAddress"),
            tooltip: t("fit:AddressModeExternalAddressTooltip"),
          }
        : hasBranch
          ? {
              label: t("fit:AddressModeBranch"),
              tooltip: t("fit:AddressModeBranchTooltip"),
            }
          : null;

  const copyAddressFromBranch = (): void => {
    if (!selectedBranch?.address) {
      return;
    }

    const addressValues = branchToAddressFormValues(selectedBranch);

    for (const field of ADDRESS_FIELDS) {
      form.setValue(`${addressField}.${field}`, addressValues[field], FORM_UPDATE_OPTIONS);
    }
  };

  return (
    <Card depth={1} className="shadow-none">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          {t(titleKey)}
          {addressModeBadge && (
            <Badge
              variant={BadgeVariant.Outlined}
              colorVariant={BadgeColorVariant.Primary}
              tooltip={addressModeBadge.tooltip}
            >
              {addressModeBadge.label}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <FormField
          control={form.control}
          name={branchField}
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>{t(branchLabelKey)}</FormLabel>
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <Combobox
                    options={branchOptions}
                    value={field.value || ""}
                    onChange={(value) =>
                      // Only the current branch is optional; the schema expects null instead of an empty string there.
                      field.onChange(kind === RelocationOrderLocationKind.Current ? value || null : value)
                    }
                    isLoading={branchComboboxState?.isLoading}
                    onSearchChange={branchComboboxState?.onSearchChange}
                    shouldFilter={!branchComboboxState}
                    hasMore={branchComboboxState?.hasMore}
                    remainingCount={branchComboboxState?.remainingCount}
                    isLoadingMore={branchComboboxState?.isLoadingMore}
                    onLoadMore={branchComboboxState?.onLoadMore}
                    placeholder={t(branchPlaceholderKey)}
                    searchPlaceholder={t("common:SearchBranches")}
                    emptyText={t("common:NoBranchesFound")}
                    clearLabel={t("common:ClearSelection")}
                  />
                </div>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant={ButtonVariant.Outlined}
                      colorVariant={ButtonColorVariant.Secondary}
                      size={ButtonSize.Icon}
                      className="h-9 w-9 shrink-0"
                      onClick={copyAddressFromBranch}
                      disabled={!canCopyAddressFromBranch}
                      aria-label={t("fit:CopyAddress")}
                    >
                      <Copy />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("fit:CopyAddress")}</TooltipContent>
                </Tooltip>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <AddressFormFields prefix={addressField} showCard={false} showRequiredIndicators={false} />
      </CardContent>
    </Card>
  );
}
